/**
 * B2BInventoryMovementsPage — תנועות מלאי
 *
 * Mobile-first inventory movements report for B2B agents.
 * Loads the warehouse list first via `b2b_agent_warehouses`, then fetches
 * movements per-warehouse via `b2b_agent_inventory_movements` with p_warehouse_id.
 * This avoids the PostgREST 1000-row limit that caused silent truncation for
 * master agents who see all warehouses.
 */

import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ChangeEvent,
} from 'react';
import {
  Package,
  RefreshCw,
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  Download,
  Printer,
  ArrowDownCircle,
  ArrowUpCircle,
  ArrowRightLeft,
  Loader2,
  AlertCircle,
  PackageOpen,
  Warehouse,
  X,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useB2BUser } from './B2BUserContext';

// ─── Types ───────────────────────────────────────────────────────────────────

interface InventoryMovement {
  movement_id: string;
  document_id: string;
  document_number: string;
  document_type_name: string;
  document_type_code: string;
  inventory_effect: string;
  issue_date: string;
  product_id: string;
  sku: string | null;
  product_name: string;
  image_url: string | null;
  warehouse_id: string;
  warehouse_name: string;
  destination_wh_id: string | null;
  destination_wh_name: string | null;
  quantity: number;
  delta: number;
  unit_price: number;
  line_total: number;
  supplier_name: string | null;
  client_name: string | null;
}

interface WarehouseOption {
  id: string;
  name: string;
}

type EffectFilter = 'all' | 'in' | 'out' | 'transfer';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function thirtyDaysAgoISO(): string {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}

function formatDateHebrew(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function formatNumber(n: number): string {
  return n.toLocaleString('he-IL');
}

function effectBadge(effect: string): { label: string; bg: string; text: string } {
  switch (effect) {
    case 'in':
      return { label: 'כניסה', bg: 'bg-emerald-50', text: 'text-emerald-700' };
    case 'out':
      return { label: 'יציאה', bg: 'bg-red-50', text: 'text-red-700' };
    case 'transfer':
      return { label: 'העברה', bg: 'bg-blue-50', text: 'text-blue-700' };
    case 'adjustment':
      return { label: 'התאמה', bg: 'bg-amber-50', text: 'text-amber-700' };
    default:
      return { label: effect, bg: 'bg-gray-50', text: 'text-gray-700' };
  }
}

// ─── CSV Export (with BOM for Hebrew Excel) ──────────────────────────────────

function exportCSV(movements: InventoryMovement[]) {
  const BOM = '\uFEFF';
  const headers = [
    'תאריך',
    'סוג מסמך',
    'מספר מסמך',
    'כיוון',
    'מקט',
    'שם מוצר',
    'מחסן',
    'מחסן יעד',
    'כמות',
    'דלתא',
    'מחיר יחידה',
    'סה"כ שורה',
    'ספק',
    'לקוח',
  ];
  const rows = movements.map(m => [
    formatDateHebrew(m.issue_date),
    m.document_type_name,
    m.document_number,
    effectBadge(m.inventory_effect).label,
    m.sku ?? '',
    m.product_name,
    m.warehouse_name,
    m.destination_wh_name ?? '',
    m.quantity,
    m.delta,
    m.unit_price,
    m.line_total,
    m.supplier_name ?? '',
    m.client_name ?? '',
  ]);

  const csv =
    BOM +
    headers.join(',') +
    '\n' +
    rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `inventory_movements_${todayISO()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── PDF Export ──────────────────────────────────────────────────────────────

async function exportMovementsPDF(tableEl: HTMLElement | null) {
  if (!tableEl) return;
  tableEl.style.display = 'block';
  try {
    const { exportTableToPDF } = await import('../../lib/pdfExport');
    await exportTableToPDF(tableEl, `inventory_movements_${todayISO()}.pdf`, { orientation: 'landscape' });
  } finally {
    tableEl.style.display = '';
  }
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function B2BInventoryMovementsPage() {
  const { session } = useB2BUser();

  const companyId = session?.activeCompanyId ?? null;
  const activeClient = session?.activeClientRecord ?? null;
  const clientWarehouseId = activeClient?.consignment_warehouse_id ?? null;
  const vanWarehouseId = session?.agentRecord?.default_warehouse_id ?? null;

  const printTableRef = useRef<HTMLDivElement>(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  // ── Warehouse state (loaded first) ─────────────────────────────────────────
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);
  const [warehousesLoading, setWarehousesLoading] = useState(true);
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>('');

  // ── Data state ──────────────────────────────────────────────────────────────
  const [movements, setMovements] = useState<InventoryMovement[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Filter state ────────────────────────────────────────────────────────────
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [fromDate, setFromDate] = useState<string>(thirtyDaysAgoISO());
  const [toDate, setToDate] = useState<string>(todayISO());
  const [effectFilter, setEffectFilter] = useState<EffectFilter>('all');
  const [productSearch, setProductSearch] = useState('');

  // ── Step 1: Fetch warehouse list (only when no client is active) ───────────
  const fetchWarehouses = useCallback(async () => {
    if (!companyId) return;
    if (activeClient) {
      setWarehousesLoading(false);
      return;
    }
    setWarehousesLoading(true);
    const { data: result, error: rpcError } = await supabase.rpc(
      'b2b_agent_warehouses',
      { p_company_id: companyId }
    );
    setWarehousesLoading(false);
    if (rpcError) {
      setError(rpcError.message);
      return;
    }
    const whs: WarehouseOption[] = (result ?? []).map((r: { warehouse_id: string; warehouse_name: string }) => ({
      id: r.warehouse_id,
      name: r.warehouse_name,
    }));
    setWarehouses(whs);
    const first = whs[0];
    if (first && !selectedWarehouse) {
      setSelectedWarehouse(first.id);
    }
  }, [companyId, selectedWarehouse, activeClient]);

  // ── When a client is active, auto-select their warehouse ───────────────────
  type WarehouseView = 'client' | 'van';
  const [warehouseView, setWarehouseView] = useState<WarehouseView>('client');

  useEffect(() => {
    if (activeClient && clientWarehouseId) {
      setSelectedWarehouse(clientWarehouseId);
      setWarehouseView('client');
      setWarehousesLoading(false);
    }
  }, [activeClient, clientWarehouseId]);

  const handleToggleView = useCallback((view: WarehouseView) => {
    setWarehouseView(view);
    if (view === 'client' && clientWarehouseId) {
      setSelectedWarehouse(clientWarehouseId);
    } else if (view === 'van' && vanWarehouseId) {
      setSelectedWarehouse(vanWarehouseId);
    }
  }, [clientWarehouseId, vanWarehouseId]);

  useEffect(() => {
    fetchWarehouses();
  }, [fetchWarehouses]);

  // ── Step 2: Fetch movements for selected warehouse ─────────────────────────
  const fetchMovements = useCallback(async () => {
    if (!companyId || !selectedWarehouse) return;
    setLoading(true);
    setError(null);

    const params: Record<string, unknown> = {
      p_company_id: companyId,
      p_warehouse_id: selectedWarehouse,
    };
    if (fromDate) params.p_from_date = fromDate;
    if (toDate) params.p_to_date = toDate;
    if (effectFilter !== 'all') params.p_effect_filter = effectFilter;

    const { data, error: rpcError } = await supabase.rpc(
      'b2b_agent_inventory_movements',
      params,
    );

    setLoading(false);
    if (rpcError) {
      setError(rpcError.message);
      return;
    }
    setMovements((data as InventoryMovement[]) ?? []);
  }, [companyId, selectedWarehouse, fromDate, toDate, effectFilter]);

  useEffect(() => {
    fetchMovements();
  }, [fetchMovements]);

  // ── Refresh ────────────────────────────────────────────────────────────────
  const handleRefresh = useCallback(async () => {
    await fetchWarehouses();
    await fetchMovements();
  }, [fetchWarehouses, fetchMovements]);

  // ── Client-side product search ──────────────────────────────────────────────
  const filteredMovements = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    if (!q) return movements;
    return movements.filter(
      m =>
        m.product_name.toLowerCase().includes(q) ||
        (m.sku ?? '').toLowerCase().includes(q),
    );
  }, [movements, productSearch]);

  // ── Summary calculations ────────────────────────────────────────────────────
  const summary = useMemo(() => {
    let totalIn = 0;
    let totalOut = 0;
    for (const m of filteredMovements) {
      if (m.delta > 0) totalIn += m.delta;
      else if (m.delta < 0) totalOut += m.delta;
    }
    return {
      totalIn,
      totalOut,
      net: totalIn + totalOut,
    };
  }, [filteredMovements]);

  // ── Effect toggle buttons config ────────────────────────────────────────────
  const effectOptions: { value: EffectFilter; label: string }[] = [
    { value: 'all', label: 'הכל' },
    { value: 'in', label: 'כניסה' },
    { value: 'out', label: 'יציאה' },
    { value: 'transfer', label: 'העברה' },
  ];

  // ── Guard ───────────────────────────────────────────────────────────────────
  if (!session) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
        <Loader2 className="w-5 h-5 animate-spin ml-2" />
        טוען…
      </div>
    );
  }

  const isInitialLoading = warehousesLoading || (loading && movements.length === 0 && !error);

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-2xl mx-auto p-4 pb-12 space-y-4" dir="rtl">
      {/* ── Header ── */}
      <div className="flex items-center justify-between pt-2">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-100 rounded-xl">
            <Package className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-900">תנועות מלאי</h1>
            <p className="text-xs text-gray-500">
              {session.activeClientRecord?.name ?? session.agentRecord?.name ?? ''}
            </p>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading || warehousesLoading}
          className="p-2 rounded-xl border border-gray-200 hover:bg-gray-50
                     transition-colors disabled:opacity-50"
          title="רענן"
        >
          <RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* ── Warehouse toggle (when a client is active) ── */}
      {activeClient && clientWarehouseId && (
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
          <button
            onClick={() => handleToggleView('client')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium rounded-lg transition-all
              ${warehouseView === 'client'
                ? 'bg-white text-orange-700 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'}`}
          >
            <Warehouse className="w-3.5 h-3.5" />
            מחסן לקוח
          </button>
          <button
            onClick={() => handleToggleView('van')}
            disabled={!vanWarehouseId}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium rounded-lg transition-all
              disabled:opacity-40 disabled:cursor-not-allowed
              ${warehouseView === 'van'
                ? 'bg-white text-orange-700 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'}`}
          >
            <Package className="w-3.5 h-3.5" />
            הרכב שלי
          </button>
        </div>
      )}

      {/* ── Warehouse selector (only when NO client is active) ── */}
      {!activeClient && warehouses.length > 0 && (
        <div className="relative">
          <Warehouse className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <select
            value={selectedWarehouse}
            onChange={e => setSelectedWarehouse(e.target.value)}
            className="w-full pr-9 pl-4 py-2.5 border border-gray-200 rounded-xl text-sm appearance-none
                       focus:outline-none focus:ring-2 focus:ring-orange-400 bg-gray-50"
          >
            {warehouses.map(w => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>
      )}

      {/* ── Summary chips ── */}
      {!isInitialLoading && !error && filteredMovements.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 rounded-full text-sm font-medium text-emerald-700">
            <ArrowDownCircle className="w-3.5 h-3.5" />
            <span>כניסה: {formatNumber(summary.totalIn)}</span>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 rounded-full text-sm font-medium text-red-700">
            <ArrowUpCircle className="w-3.5 h-3.5" />
            <span>יציאה: {formatNumber(Math.abs(summary.totalOut))}</span>
          </div>
          <div
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium
              ${summary.net >= 0 ? 'bg-blue-50 text-blue-700' : 'bg-orange-50 text-orange-700'}`}
          >
            <ArrowRightLeft className="w-3.5 h-3.5" />
            <span>
              נטו: {summary.net >= 0 ? '+' : ''}
              {formatNumber(summary.net)}
            </span>
          </div>
        </div>
      )}

      {/* ── Product search ── */}
      <div className="relative">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          type="text"
          value={productSearch}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setProductSearch(e.target.value)}
          placeholder="חיפוש לפי שם מוצר או מקט…"
          className="w-full pr-9 pl-4 py-2.5 border border-gray-200 rounded-xl text-sm
                     focus:outline-none focus:ring-2 focus:ring-orange-400"
        />
        {productSearch && (
          <button
            onClick={() => setProductSearch('')}
            className="absolute left-3 top-1/2 -translate-y-1/2"
          >
            <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
          </button>
        )}
      </div>

      {/* ── Filter toggle + export buttons ── */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setFiltersOpen(prev => !prev)}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-sm font-medium
                      transition-colors
                      ${filtersOpen
                        ? 'border-orange-400 bg-orange-50 text-orange-700'
                        : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
        >
          <Filter className="w-4 h-4" />
          סינון
          {filtersOpen ? (
            <ChevronUp className="w-3.5 h-3.5" />
          ) : (
            <ChevronDown className="w-3.5 h-3.5" />
          )}
        </button>
        <div className="flex-1" />
        <button
          onClick={() => exportCSV(filteredMovements)}
          disabled={filteredMovements.length === 0}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200
                     text-sm text-gray-600 hover:bg-gray-50 transition-colors
                     disabled:opacity-40 disabled:cursor-not-allowed"
          title="ייצוא ל-Excel"
        >
          <Download className="w-4 h-4" />
          <span className="hidden sm:inline">Excel</span>
        </button>
        <button
          onClick={async () => {
            setPdfBusy(true);
            try { await exportMovementsPDF(printTableRef.current); } finally { setPdfBusy(false); }
          }}
          disabled={filteredMovements.length === 0 || pdfBusy}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200
                     text-sm text-gray-600 hover:bg-gray-50 transition-colors
                     disabled:opacity-40 disabled:cursor-not-allowed"
          title="הורדה כ-PDF"
        >
          {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
          <span className="hidden sm:inline">{pdfBusy ? 'מייצר…' : 'PDF'}</span>
        </button>
      </div>

      {/* ── Collapsible filter panel ── */}
      {filtersOpen && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 space-y-4
                        animate-in slide-in-from-top-2">
          {/* Date range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">מתאריך</label>
              <input
                type="date"
                value={fromDate}
                onChange={e => setFromDate(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm
                           focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">עד תאריך</label>
              <input
                type="date"
                value={toDate}
                onChange={e => setToDate(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm
                           focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
            </div>
          </div>

          {/* Effect direction toggle */}
          <div>
            <label className="text-xs text-gray-500 mb-1 block">כיוון תנועה</label>
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {effectOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setEffectFilter(opt.value)}
                  className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-colors
                    ${effectFilter === opt.value
                      ? 'bg-white text-orange-700 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Loading state ── */}
      {isInitialLoading && (
        <div className="flex items-center justify-center py-12 gap-2 text-gray-400 text-sm">
          <Loader2 className="w-5 h-5 animate-spin" />
          טוען תנועות מלאי…
        </div>
      )}

      {/* Subtle inline loader when switching warehouses */}
      {loading && !isInitialLoading && (
        <div className="flex items-center justify-center py-3 gap-2 text-gray-400 text-xs">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>טוען נתוני מחסן…</span>
        </div>
      )}

      {/* ── Error state ── */}
      {error && !loading && (
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {/* ── No warehouses state ── */}
      {!warehousesLoading && !error && warehouses.length === 0 && !activeClient && (
        <div className="text-center py-16 text-gray-400">
          <Warehouse className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">לא נמצאו מחסנים המשויכים אליך</p>
        </div>
      )}

      {/* ── Empty state ── */}
      {!isInitialLoading && !loading && !error && filteredMovements.length === 0 && selectedWarehouse && (
        <div className="text-center py-16 text-gray-400">
          <PackageOpen className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">
            {movements.length === 0
              ? 'לא נמצאו תנועות מלאי בטווח התאריכים הנבחר'
              : 'אין תוצאות לחיפוש'}
          </p>
        </div>
      )}

      {/* ── Results count ── */}
      {!isInitialLoading && !loading && !error && filteredMovements.length > 0 && (
        <p className="text-xs text-gray-400">
          {filteredMovements.length === movements.length
            ? `${formatNumber(filteredMovements.length)} תנועות`
            : `${formatNumber(filteredMovements.length)} מתוך ${formatNumber(movements.length)} תנועות`}
        </p>
      )}

      {/* ── Movement cards ── */}
      {!isInitialLoading && !loading && (
        <div className="space-y-2">
          {filteredMovements.map(m => {
            const badge = effectBadge(m.inventory_effect);
            const isPositive = m.delta > 0;
            const isNegative = m.delta < 0;

            return (
              <div
                key={m.movement_id}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4
                           transition-colors hover:border-gray-200"
              >
                {/* Top row: date + effect badge + document number */}
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${badge.bg} ${badge.text}`}>
                    {m.document_type_name || badge.label}
                  </span>
                  <span className="text-xs text-gray-400 font-mono" dir="ltr">
                    {formatDateHebrew(m.issue_date)}
                  </span>
                </div>

                {/* Product info */}
                <div className="flex items-start gap-3">
                  {/* Product image */}
                  {m.image_url ? (
                    <img
                      src={m.image_url}
                      alt={m.product_name}
                      className="w-10 h-10 rounded-lg object-cover shrink-0 border border-gray-100"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                      <PackageOpen className="w-5 h-5 text-gray-300" />
                    </div>
                  )}

                  {/* Product details + delta */}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-900 truncate">
                      {m.product_name}
                    </div>
                    {m.sku && (
                      <div className="font-mono text-xs text-gray-400 mt-0.5">{m.sku}</div>
                    )}
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-xs text-gray-500">{m.warehouse_name}</span>
                      {m.destination_wh_name && (
                        <>
                          <span className="text-xs text-gray-300">←</span>
                          <span className="text-xs text-gray-500">{m.destination_wh_name}</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Delta quantity */}
                  <div className="shrink-0 text-left">
                    <div
                      className={`text-base font-bold tabular-nums
                        ${isPositive ? 'text-emerald-600' : isNegative ? 'text-red-600' : 'text-gray-600'}`}
                      dir="ltr"
                    >
                      {isPositive ? '+' : ''}
                      {formatNumber(m.delta)}
                    </div>
                    {m.line_total !== 0 && (
                      <div className="text-xs text-gray-400 mt-0.5 tabular-nums" dir="ltr">
                        ₪{formatNumber(m.line_total)}
                      </div>
                    )}
                  </div>
                </div>

                {/* Bottom row: document number + counterparty */}
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-50">
                  <span className="text-xs text-gray-400">
                    מסמך #{m.document_number}
                  </span>
                  {(m.supplier_name || m.client_name) && (
                    <span className="text-xs text-gray-400 truncate max-w-[50%]">
                      {m.supplier_name ?? m.client_name}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {/* ── Hidden PDF table ── */}
      {!loading && !error && filteredMovements.length > 0 && (
        <div ref={printTableRef} className="hidden" dir="rtl">
          <h2 style={{ textAlign: 'center', fontSize: 18, fontWeight: 'bold', marginBottom: 8 }}>תנועות מלאי</h2>
          <p style={{ textAlign: 'center', fontSize: 12, color: '#666', marginBottom: 12 }}>
            {formatDateHebrew(fromDate)} – {formatDateHebrew(toDate)}
          </p>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #999' }}>
                <th style={{ padding: '6px 4px', textAlign: 'right' }}>תאריך</th>
                <th style={{ padding: '6px 4px', textAlign: 'right' }}>סוג מסמך</th>
                <th style={{ padding: '6px 4px', textAlign: 'right' }}>מספר</th>
                <th style={{ padding: '6px 4px', textAlign: 'right' }}>כיוון</th>
                <th style={{ padding: '6px 4px', textAlign: 'right' }}>מקט</th>
                <th style={{ padding: '6px 4px', textAlign: 'right' }}>שם מוצר</th>
                <th style={{ padding: '6px 4px', textAlign: 'right' }}>מחסן</th>
                <th style={{ padding: '6px 4px', textAlign: 'left' }}>דלתא</th>
              </tr>
            </thead>
            <tbody>
              {filteredMovements.map(m => (
                <tr key={`pdf-${m.movement_id}`} style={{ borderBottom: '1px solid #ddd' }}>
                  <td style={{ padding: '4px' }}>{formatDateHebrew(m.issue_date)}</td>
                  <td style={{ padding: '4px' }}>{m.document_type_name}</td>
                  <td style={{ padding: '4px' }}>{m.document_number}</td>
                  <td style={{ padding: '4px' }}>{effectBadge(m.inventory_effect).label}</td>
                  <td style={{ padding: '4px', fontFamily: 'monospace', fontSize: 11 }}>{m.sku ?? '—'}</td>
                  <td style={{ padding: '4px' }}>{m.product_name}</td>
                  <td style={{ padding: '4px' }}>{m.warehouse_name}</td>
                  <td style={{ padding: '4px', textAlign: 'left', fontWeight: 600,
                    color: m.delta > 0 ? '#059669' : m.delta < 0 ? '#dc2626' : '#666' }}>
                    {m.delta > 0 ? '+' : ''}{formatNumber(m.delta)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid #666', fontWeight: 'bold' }}>
                <td style={{ padding: '6px 4px' }} colSpan={7}>
                  סה״כ ({formatNumber(filteredMovements.length)} תנועות)
                </td>
                <td style={{ padding: '6px 4px', textAlign: 'left' }}>
                  נטו: {summary.net >= 0 ? '+' : ''}{formatNumber(summary.net)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

export default B2BInventoryMovementsPage;
